import React, { useCallback, useMemo } from 'react';
import { DragHandle } from '@mui/icons-material';
import { UseQueryResult } from 'react-query';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { IMenuAction } from '~/Models/interfaces/menuAction.models';
import { IFitoListItem } from '../../models';
import { IFitoList, IFitoListSettings, IFitoListTab } from '../interfaces';
import { filterList } from '../tools';

interface IFitoListFiltersTabsSearchProps<T> {
  unstructuredList: T[];
  settings: IFitoListSettings;
  searchText: string;
  isBeingRearranged: boolean;
  searchResponse?: UseQueryResult<T[], IErrorResponse>;
  setPage: (page: number) => void;
  enableForceRefresh: () => void;
  setItemValues: (item: T) => IFitoList;
  actions?: IMenuAction[] | ((item: T) => IMenuAction[]);
  keyOfId: keyof T;
  tabs?: IFitoListTab[];
  setSelectedTabIndex: (index: number) => void;
  getItemActions?: (item: T) => IMenuAction[];
}

export const useFitoListFiltersTabsSearch = <T extends object>({
  unstructuredList,
  settings,
  searchText,
  isBeingRearranged,
  searchResponse,
  setPage,
  setSelectedTabIndex,
  enableForceRefresh,
  setItemValues,
  actions,
  keyOfId,
  tabs,
  // this is for the uncontrolled list (not with query)
  getItemActions,
}: IFitoListFiltersTabsSearchProps<T>) => {
  // this is for the uncontrolled list (not with query)
  const generateActions = useCallback(
    (item: T): IMenuAction[] => {
      if (getItemActions) {
        if (actions) {
          return (actions as IMenuAction[]).concat(...(getItemActions?.(item) ?? []));
        }
        return getItemActions(item);
      }
      if (typeof actions === 'function') {
        return (actions as (item: T) => IMenuAction[])(item) ?? [];
      }
      return actions ?? [];
    },
    [actions, getItemActions],
  );

  const initializeStructuredList = (list: T[]): IFitoListItem[] =>
    list.map((item) => ({
      ...setItemValues(item),
      checkable: !!settings.select,
      notificationIcon: isBeingRearranged ? <DragHandle /> : undefined,
      actions: generateActions(item),
      id: `${item[keyOfId] as any}`,
    }));

  const filteredList = useMemo(
    () =>
      filterList(
        unstructuredList,
        settings,
        !!searchResponse?.data?.length ? '' : searchText,
        initializeStructuredList,
      ),
    [unstructuredList, settings, searchText, isBeingRearranged, searchResponse?.data],
  );
  const tabbedFilters = useMemo(() => tabs?.map((tab) => tab.name), [tabs]);
  const onTabbedFilterChange = useCallback(
    (index: number) => {
      setSelectedTabIndex(index);
      setPage(settings?.paginationSettings?.startAt ?? 1);
      enableForceRefresh?.();
    },
    [setSelectedTabIndex, setPage, enableForceRefresh],
  );

  return {
    filteredList,
    tabbedFilters,
    onTabbedFilterChange,
  };
};
